/**
 * Models (plan E10-T07): the tenant's own model endpoints. Register one, check it with Swaralo's
 * fixtures (the report: passed or not, how long it took, what came back), and remove it. Agents,
 * collections and Call Scripts name an endpoint as `provider: endpoint`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Plus, Trash2, XCircle } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { DefaultModelsSection } from "../agents/builder/DefaultModels";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Endpoint = Schemas["ModelEndpointOut"];
type Kind = Endpoint["kind"];
type Protocol = Endpoint["protocol"];
type Report = Schemas["ModelEndpointTest"];

const KINDS: Kind[] = ["stt", "llm", "tts", "embedding", "classifier"];
const STREAMING: Kind[] = ["stt", "tts"]; // the kinds swaralo-ws serves
const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";
/** The price a kind is billed by, and its label. */
const PRICES: Record<Kind, [keyof Schemas["ModelEndpointPrices"], MessageId][]> = {
  stt: [["per_minute", "models.price.perMinute"]],
  llm: [["per_million_input_tokens", "models.price.input"], ["per_million_output_tokens", "models.price.output"]],
  tts: [["per_million_characters", "models.price.characters"]],
  embedding: [["per_million_input_tokens", "models.price.input"]],
  classifier: [["per_million_input_tokens", "models.price.input"], ["per_million_output_tokens", "models.price.output"]],
};

export function ModelsPage() {
  const t = useT();
  const canWrite = useCan("agents:write");
  const [adding, setAdding] = useState(false);
  const endpoints = useApiQuery("model-endpoints", (api) => api.GET("/v1/model-endpoints", { params: { query: { limit: 200 } } }));
  // what latency routing sees (V1-E15): refreshed as calls measure the providers
  const measured = useApiQuery("provider-metrics", (api) => api.GET("/v1/providers/metrics"), { refetchInterval: 30_000 });
  const register = canWrite && !adding && (
    <Button onClick={() => setAdding(true)}>
      <Plus size={16} aria-hidden="true" />
      {t("models.new")}
    </Button>
  );
  return (
    <Page title={t("nav.models")} description={t("models.description")} actions={register}>
      <DefaultModelsSection />
      {adding && <NewEndpoint onDone={() => setAdding(false)} />}
      <Section query={endpoints}>
        {(page) =>
          page.data.length === 0 ? (
            adding ? null : (
              <Empty title={t("models.empty")}>
                <p>{t("models.emptyBody")}</p>
                {register}
              </Empty>
            )
          ) : (
            <Endpoints rows={page.data} canWrite={canWrite} />
          )
        }
      </Section>
      <Section title={t("models.metrics")} query={measured}>
        {(found) => <Metrics rows={found.data} />}
      </Section>
    </Page>
  );
}

type Measured = Schemas["ProviderStats"];

/** The providers' last 15 minutes: what `latency` routing ranks by, and their failures. */
function Metrics({ rows }: { rows: Measured[] }) {
  const t = useT();
  const ms = (v: number | null) => (v === null ? "—" : t("models.ms", { ms: v }));
  if (rows.length === 0) return <p className="text-sm text-[var(--color-muted)]">{t("models.metricsEmpty")}</p>;
  return (
    <>
      <p className="text-sm text-[var(--color-muted)]">{t("models.metricsBody")}</p>
      <Table
        caption={t("models.metrics")}
        rows={rows}
        rowKey={(r) => `${r.role}:${r.provider}:${r.model ?? ""}`}
        columns={[
          { header: t("models.metrics.role"), cell: (r) => r.role.toUpperCase() },
          { header: t("models.metrics.provider"), cell: (r) => <span className="font-medium">{r.provider}</span> },
          { header: t("models.metrics.model"), cell: (r) => r.model ?? "—" },
          { header: t("models.metrics.turns"), cell: (r) => r.samples },
          { header: t("models.metrics.p50"), cell: (r) => (r.measured ? ms(r.p50_ms) : t("models.metrics.unmeasured")) },
          { header: t("models.metrics.p95"), cell: (r) => ms(r.p95_ms) },
          { header: t("models.metrics.failures"), cell: (r) => t("models.metrics.failed", { failures: r.failures, attempts: r.attempts }) },
        ]}
      />
    </>
  );
}

function Endpoints({ rows, canWrite }: { rows: Endpoint[]; canWrite: boolean }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const [reports, setReports] = useState<Record<string, Report>>({});
  const refresh = () => queries.invalidateQueries({ queryKey: ["model-endpoints", tenant] });
  const check = useMutation({
    mutationFn: (e: Endpoint) => data(api.POST("/v1/model-endpoints/{endpoint_id}/test", { params: { path: { endpoint_id: e.id } }, body: {} })),
    onSuccess: async (report, e) => {
      setReports((r) => ({ ...r, [e.id]: report }));
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (e: Endpoint) => data(api.DELETE("/v1/model-endpoints/{endpoint_id}", { params: { path: { endpoint_id: e.id } } })),
    onSuccess: refresh,
  });
  return (
    <div className="space-y-3">
      {(check.isError || remove.isError) && <ErrorState error={check.error ?? remove.error} />}
      <Table
        caption={t("models.endpoints")}
        rows={rows}
        rowKey={(e) => e.id}
        columns={[
          { header: t("models.name"), cell: (e) => <span className="font-medium">{e.name}</span> },
          { header: t("models.kind"), cell: (e) => t(`models.kind.${e.kind}` as MessageId) },
          { header: t("models.protocol"), cell: (e) => <code className="text-xs">{e.protocol}</code> },
          { header: t("models.url"), cell: (e) => <span className="break-all font-mono text-xs">{e.url}</span> },
          { header: t("models.region"), cell: (e) => e.region ?? "—" },
          { header: t("models.lastCheck"), cell: (e) => <CheckReport report={reports[e.id] ?? e.last_test ?? null} /> },
          ...(canWrite ? [{
            header: t("models.actions"),
            cell: (e: Endpoint) => (
              <div className="flex gap-2">
                <Button variant="secondary" disabled={check.isPending} onClick={() => check.mutate(e)}
                  aria-label={t("models.checkOne", { name: e.name })}>
                  {check.isPending && check.variables?.id === e.id ? t("models.checking") : t("models.check")}
                </Button>
                <Button variant="ghost" aria-label={t("models.remove", { name: e.name })}
                  onClick={() => { if (window.confirm(t("models.removeConfirm", { name: e.name }))) remove.mutate(e); }}>
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              </div>
            ),
          }] : []),
        ]}
      />
    </div>
  );
}

/** The last check: passed or not, the time it took, and what the model did with the fixture. */
function CheckReport({ report }: { report: Report | null }) {
  const t = useT();
  const format = useFormat();
  if (!report) return <span className="text-[var(--color-muted)]">{t("models.notChecked")}</span>;
  const Icon = report.ok ? CheckCircle2 : XCircle;
  return (
    <div className="space-y-0.5 text-sm" role="status">
      <span className={`inline-flex items-center gap-1 font-medium ${report.ok ? "text-[var(--color-success)]" : "text-[var(--color-danger)]"}`}>
        <Icon size={16} aria-hidden="true" />
        {report.ok ? t("models.passed") : t("models.failed")}
        <span className="font-normal text-[var(--color-muted)]">{t("models.took", { ms: format.number(report.latency_ms) })}</span>
      </span>
      <p className="text-xs">{report.detail}</p>
      {report.sample && <p className="text-xs text-[var(--color-muted)]">“{report.sample}”</p>}
    </div>
  );
}

/** Where the model is, how to speak to it, its credential, region and prices. */
function NewEndpoint({ onDone }: { onDone: () => void }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const id = useId();
  const credentials = useApiQuery("credentials", (a) => a.GET("/v1/credentials", { params: { query: { limit: 200 } } }));
  const [form, setForm] = useState({ name: "", kind: "llm" as Kind, protocol: "openai-compatible" as Protocol, url: "", model: "",
    credential: "", region: "", prices: {} as Record<string, string> });
  const streaming = STREAMING.includes(form.kind);
  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/model-endpoints", {
      body: {
        name: form.name.trim(), kind: form.kind, protocol: streaming ? form.protocol : "openai-compatible", url: form.url.trim(),
        model: form.model.trim() || null, credential: form.credential || null, region: form.region.trim() || null,
        prices: Object.fromEntries(PRICES[form.kind].filter(([key]) => form.prices[key]).map(([key]) => [key, Number(form.prices[key])])),
      },
    })),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ["model-endpoints", tenant] });
      onDone();
    },
  });
  const ready = /^[a-z0-9][a-z0-9-]{0,62}$/.test(form.name.trim()) && form.url.trim();
  const input = (key: "name" | "url" | "model" | "region", label: MessageId, hint?: MessageId, mono = true) => (
    <div className="space-y-1">
      <label htmlFor={`${id}-${key}`} className="block text-sm font-medium">{t(label)}</label>
      <input id={`${id}-${key}`} className={`${field} ${mono ? "font-mono" : ""}`} value={form[key]} spellCheck={false}
        aria-describedby={hint ? `${id}-${key}-hint` : undefined} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      {hint && <p id={`${id}-${key}-hint`} className="text-xs text-[var(--color-muted)]">{t(hint)}</p>}
    </div>
  );
  return (
    <form aria-label={t("models.new")} className="mb-4 space-y-3 rounded-lg border border-[var(--color-border)] p-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready) create.mutate();
      }}>
      <div className="grid gap-3 sm:grid-cols-2">
        {input("name", "models.name", "models.nameHint")}
        <div className="space-y-1">
          <label htmlFor={`${id}-kind`} className="block text-sm font-medium">{t("models.kind")}</label>
          <select id={`${id}-kind`} className={field} value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as Kind, prices: {} })}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`models.kind.${k}` as MessageId)}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-protocol`} className="block text-sm font-medium">{t("models.protocol")}</label>
          <select id={`${id}-protocol`} className={field} value={streaming ? form.protocol : "openai-compatible"} disabled={!streaming}
            aria-describedby={`${id}-protocol-hint`} onChange={(e) => setForm({ ...form, protocol: e.target.value as Protocol })}>
            <option value="openai-compatible">{t("models.protocol.openai")}</option>
            {streaming && <option value="swaralo-ws">{t("models.protocol.ws")}</option>}
          </select>
          <p id={`${id}-protocol-hint`} className="text-xs text-[var(--color-muted)]">{t("models.protocolHint")}</p>
        </div>
        {input("url", "models.url", form.protocol === "swaralo-ws" && streaming ? "models.urlHintWs" : "models.urlHint")}
        {input("model", "models.model")}
        <div className="space-y-1">
          <label htmlFor={`${id}-credential`} className="block text-sm font-medium">{t("models.credential")}</label>
          <select id={`${id}-credential`} className={field} value={form.credential}
            onChange={(e) => setForm({ ...form, credential: e.target.value })}>
            <option value="">{t("models.noCredential")}</option>
            {(credentials.data?.data ?? []).map((c: Schemas["CredentialOut"]) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        {input("region", "models.region", "models.regionHint", false)}
        {PRICES[form.kind].map(([key, label]) => (
          <div key={key} className="space-y-1">
            <label htmlFor={`${id}-${key}`} className="block text-sm font-medium">{t(label)}</label>
            <input id={`${id}-${key}`} type="number" min={0} step="any" className={field} value={form.prices[key] ?? ""}
              onChange={(e) => setForm({ ...form, prices: { ...form.prices, [key]: e.target.value } })} />
          </div>
        ))}
      </div>
      {create.isError && <ErrorState error={create.error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={!ready || create.isPending}>{t("models.register")}</Button>
        <Button variant="ghost" onClick={onDone}>{t("models.cancel")}</Button>
      </div>
    </form>
  );
}
